<template>
  <v-container
    fluid
    class="contact-section background py-8 py-md-16"
    id="contact"
  >
    <v-container>
      <v-row>
        <v-col cols="12" class="text-center mb-8">
          <span
            class="overline secondary--text font-weight-bold"
            data-aos="fade-down"
          >HABLEMOS</span>
          <h2
            class="display-1 greydark--text font-weight-bold mt-2"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Contanos sobre tu operación
          </h2>
          <p
            class="text-h6 greylight--text mt-4"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Contanos qué industria operás y cuál es tu mayor desafío operativo.
            Nuestro equipo arma una propuesta específica para vos.
          </p>
        </v-col>
      </v-row>

      <v-row class="d-flex justify-center align-center">
        <v-col
          cols="12"
          md="5"
          class="d-flex flex-column align-center justify-center"
          data-aos="fade-right"
          data-aos-delay="300"
        >
          <h3 class="text-h4 greydark--text mb-4 text-center font-weight-bold">
            ¿Por qué hablar con nosotros?
          </h3>
          <p class="text-body-1 greylight--text text-center" style="max-width: 400px;">
            Trabajamos exclusivamente con empresas que quieren soluciones
            de fondo, no parches. Si tu industria mueve operaciones
            complejas, tenemos algo concreto para mostrarte.
          </p>

          <v-list class="transparent mt-6">
            <v-list-item
              v-for="(item, index) in diagnosticFeatures"
              :key="index"
            >
              <v-list-item-icon>
                <v-icon color="accent">{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="greydark--text font-weight-medium">
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>

        <v-col
          cols="12"
          md="6"
          data-aos="fade-left"
          data-aos-delay="300"
        >
          <v-card class="pa-8 rounded-lg" elevation="5">
            <v-form ref="contactForm" @submit.prevent="submitForm">
              <v-text-field
                v-model="form.name"
                label="Nombre completo"
                outlined
                required
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'El nombre es requerido']"
              ></v-text-field>

              <v-text-field
                v-model="form.email"
                label="Correo electrónico"
                type="email"
                outlined
                required
                prepend-inner-icon="mdi-email"
                :rules="emailRules"
              ></v-text-field>

              <v-text-field
                v-model="form.subject"
                label="¿Cuál es tu proceso más caótico?"
                outlined
                prepend-inner-icon="mdi-alert-circle-outline"
                required
                placeholder="Ej: Importaciones, logística, salud, retail..."
                :rules="[v => !!v || 'Este campo es requerido']"
              ></v-text-field>

              <v-textarea
                v-model="form.message"
                label="Contanos brevemente qué te está costando tiempo o generando errores"
                outlined
                rows="4"
                prepend-inner-icon="mdi-message-text"
                required
                placeholder="Ej: Perdemos tiempo buscando facturas en
                  WhatsApp y Excel..."
                :rules="[v => !!v || 'Este campo es requerido']"
              ></v-textarea>

              <v-btn
                type="submit"
                color="secondary"
                :x-large="$vuetify.breakpoint.mdAndUp"
                :large="$vuetify.breakpoint.smAndDown"
                block
                depressed
                class="white--text"
                :loading="submitting"
              >
                Enviar consulta
              </v-btn>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog
      v-model="showSuccessDialog"
      max-width="500"
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex flex-column align-center pt-8">
          <v-avatar color="success" size="80" class="mb-4">
            <v-icon size="50" color="white">mdi-check-circle</v-icon>
          </v-avatar>
          <span class="text-h5 greydark--text font-weight-bold text-center">
            ¡Solicitud enviada con éxito!
          </span>
        </v-card-title>
        <v-card-text class="text-center px-8 pb-4">
          <p class="text-body-1 greylight--text mb-4">
            Gracias por tu interés en AdaptifyX.
            Hemos recibido tu solicitud de demo.
          </p>
          <p class="text-body-1 greydark--text font-weight-medium">
            Nuestro equipo se pondrá en contacto contigo en las
            próximas <strong>24 horas</strong> para agendar tu
            demo personalizada.
          </p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn
            color="success"
            large
            depressed
            rounded
            class="white--text px-8"
            @click="showSuccessDialog = false"
          >
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog
      v-model="showErrorDialog"
      max-width="500"
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex flex-column align-center pt-8">
          <v-avatar color="error" size="80" class="mb-4">
            <v-icon size="50" color="white">mdi-alert-circle</v-icon>
          </v-avatar>
          <span class="text-h5 greydark--text font-weight-bold text-center">
            Error al enviar
          </span>
        </v-card-title>
        <v-card-text class="text-center px-8 pb-4">
          <p class="text-body-1 greylight--text mb-4">
            Lo sentimos, ocurrió un error al enviar tu solicitud.
          </p>
          <p class="text-body-1 greydark--text">
            Por favor, intenta de nuevo o contáctanos directamente
            a <strong>contacto@adaptifyx.com</strong>.
          </p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn
            color="error"
            large
            depressed
            rounded
            class="white--text px-8"
            @click="showErrorDialog = false"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import URI from 'urijs';

export default {
  name: 'ContactForm',
  data() {
    return {
      submitting: false,
      showSuccessDialog: false,
      showErrorDialog: false,
      form: {
        name: '',
        email: '',
        subject: '',
        message: '',
      },
      emailRules: [
        (v) => !!v || 'El correo electrónico es requerido',
        (v) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(v) || 'Email debe ser válido',
      ],
      diagnosticFeatures: [
        { icon: 'mdi-domain', text: 'Especialización por industria' },
        { icon: 'mdi-check-decagram', text: 'Sin contratos largos para empezar' },
        { icon: 'mdi-lightning-bolt', text: 'Implementación en días, no meses' },
        { icon: 'mdi-headset', text: 'Acompañamiento desde el día 1' },
      ],
    };
  },
  methods: {
    submitForm() {
      if (!this.$refs.contactForm.validate()) {
        return;
      }

      this.submitting = true;

      const uri = new URI('https://contact.adaptifyx.com');
      uri.segment('items');

      const body = {
        name: this.form.name,
        email: this.form.email,
        subject: this.form.subject,
        message: this.form.message,
      };

      axios.put(uri.toString(), body, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          this.showSuccessDialog = true;
          this.resetForm();
        })
        .catch(() => {
          this.showErrorDialog = true;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    resetForm() {
      this.form = {
        name: '',
        email: '',
        subject: '',
        message: '',
      };
      this.$refs.contactForm.resetValidation();
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-section {
  position: relative;
}
</style>
